import { Code, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { TranslationKeys } from 'i18n';
import { CopyToClipboard } from 'shared/components/CopyToClipboard';

const clientAssertionType = 'urn:ietf:params:oauth:client-assertion-type:jwt-bearer';

type Props = {
    clientId: string;
    federationScope?: string | null;
    developerScope?: string | null;
    showWorkloads: boolean;
    // Tenants of the client's developer groups; developers sign in to one of these
    developerTenantIds: string[];
};

type ConnectValue = { label: TranslationKeys; value?: string | null };

export const EntraSignInHowToConnect = ({
    clientId,
    federationScope,
    developerScope,
    showWorkloads,
    developerTenantIds,
}: Props) => {
    const { t } = useTranslation();
    const tokenEndpoint = `${import.meta.env.VITE_APP_OIDC_AUTHORITY.replace(/\/$/, '')}/connect/token`;
    const showDevelopers = developerTenantIds.length > 0 && !!developerScope;

    const sharedValues: ConnectValue[] = [
        { label: 'clientId', value: clientId },
        { label: 'tokenEndpoint', value: tokenEndpoint },
        { label: 'clientAssertionType', value: clientAssertionType },
    ];

    return (
        <Paper p="md" withBorder>
            <Stack gap="xs">
                <Title order={5}>{t('howToConnect')}</Title>
                <Text c="dimmed" size="sm">
                    {t('howToConnectDescription')}
                </Text>
                <ConnectValues values={sharedValues} />
                {showWorkloads && (
                    <>
                        <Text fw={600} pt="xs" size="sm">
                            {t('howToConnectWorkloads')}
                        </Text>
                        <ConnectValues values={[{ label: 'entraTokenScope', value: federationScope }]} />
                    </>
                )}
                {showDevelopers && (
                    <>
                        <Text fw={600} pt="xs" size="sm">
                            {t('howToConnectDevelopers')}
                        </Text>
                        <Text c="dimmed" size="sm">
                            {t('howToConnectDevelopersDescription')}
                        </Text>
                        <ConnectValues
                            values={[
                                { label: 'entraTokenScope', value: developerScope },
                                ...developerTenantIds.map(
                                    (tenantId): ConnectValue => ({
                                        label: 'azureCliCommand',
                                        value: `az account get-access-token --tenant ${tenantId} --scope ${developerScope} --query accessToken -o tsv`,
                                    }),
                                ),
                            ]}
                        />
                    </>
                )}
            </Stack>
        </Paper>
    );
};

const ConnectValues = ({ values }: { values: ConnectValue[] }) => {
    const { t } = useTranslation();

    return values
        .filter((v) => !!v.value)
        .map(({ label, value }) => (
            <Group key={`${label}-${value}`} gap="xs" wrap="nowrap">
                <Text miw={180} size="sm">
                    {t(label)}
                </Text>
                <Code>{value}</Code>
                <CopyToClipboard textToCopy={value ?? ''} />
            </Group>
        ));
};
